import React, { useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import OrganizationAnalyticsChartCard from './OrganizationAnalyticsChartCard';
import { CHART_COLORS, formatValueByType, mapIdsToLabels } from './chartUtils';

export const DEFAULT_TOP_CHATTERS_LIMIT = 5;
const OTHER_CHATTERS_ID = 'other-chatters';
const OTHER_CHATTERS_LABEL = 'Other';

const OrganizationImpactPerChatterChart = ({ data, chatterLabelMap, selectedChatterIds = [], pending }) => {
  const pieData = useMemo(() => {
    const chatterIds = data?.chatterIds || [];
    const impactValues = data?.earnings || [];

    const points = chatterIds
      .map((chatterId, index) => ({
        id: chatterId,
        name: mapIdsToLabels([chatterId], chatterLabelMap)[0],
        y: Number(impactValues[index] || 0),
      }))
      .filter(point => Number.isFinite(point.y) && point.y > 0);

    if (!selectedChatterIds.length) {
      const sortedPoints = [...points].sort((firstPoint, secondPoint) => secondPoint.y - firstPoint.y);

      if (sortedPoints.length <= DEFAULT_TOP_CHATTERS_LIMIT) {
        return sortedPoints;
      }

      const topPoints = sortedPoints.slice(0, DEFAULT_TOP_CHATTERS_LIMIT);
      const otherImpact = sortedPoints.slice(DEFAULT_TOP_CHATTERS_LIMIT).reduce((sum, current) => sum + current.y, 0);

      return [...topPoints, { id: OTHER_CHATTERS_ID, name: OTHER_CHATTERS_LABEL, y: otherImpact }];
    }

    return points.filter(point => selectedChatterIds.includes(point.id));
  }, [data, chatterLabelMap, selectedChatterIds]);

  const hasData = pieData.length > 0;
  const hasOtherCategory = useMemo(() => pieData.some(point => point.id === OTHER_CHATTERS_ID), [pieData]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: 360,
        style: {
          fontFamily: 'Lexend',
        },
      },
      title: {
        text: '',
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      legend: {
        itemStyle: {
          color: '#150C2A',
          fontSize: 12,
        },
      },
      plotOptions: {
        pie: {
          borderWidth: 0,
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      colors: CHART_COLORS,
      tooltip: {
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        formatter: function () {
          const earnings = formatValueByType(this.y, 'currency');
          const percentage = formatValueByType(this.percentage, 'percent');

          return `<div style="padding:8px 12px;border-radius:16px;background:#FFF;box-shadow:0px 2px 2px 0px rgba(21, 12, 42, 0.07);border:1px solid #CBC0EE;color:#150C2A;font-size:12px;font-weight:500;"><div style="display:flex;align-items:center;gap:8px;"><span style="margin-right:auto;">${this.point.name}</span><span>${percentage}</span></div><div style="margin-top:2px;font-size:11px;font-weight:400;opacity:0.72;">Earnings: ${earnings}</div></div>`;
        },
        style: {
          pointerEvents: 'none',
        },
      },
      series: [
        {
          type: 'pie',
          colorByPoint: true,
          data: pieData,
        },
      ],
    }),
    [pieData],
  );

  return (
    <OrganizationAnalyticsChartCard
      title="Impact Per Chatter"
      subtitle={
        selectedChatterIds.length
          ? 'Impact share for the selected chatters.'
          : hasOtherCategory
            ? `Impact share for the top ${DEFAULT_TOP_CHATTERS_LIMIT} chatters and Other.`
            : `Impact share for the top ${DEFAULT_TOP_CHATTERS_LIMIT} chatters.`
      }
      pending={pending}
      hasData={hasData}
      emptyText="No chatter impact data available for the selected filters.">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </OrganizationAnalyticsChartCard>
  );
};

export default OrganizationImpactPerChatterChart;
